/**
 * Cart drawer — open/close and dynamic cart state via Section Rendering API.
 */
(function () {
  const SELECTORS = {
    drawer: '.cc-cart-drawer',
    overlay: '.cc-cart-drawer__overlay',
    panel: '.cc-cart-drawer__panel',
    body: '[data-cart-drawer-body]',
    footer: '[data-cart-drawer-footer]',
    title: '[data-cart-drawer-title]',
    openTrigger: '.js-open-cart-drawer',
    closeTrigger: '.js-close-cart-drawer',
    qtyButton: '.js-cart-drawer-qty',
    removeButton: '.js-cart-drawer-remove',
    headerCartCount: '.header-cart__count'
  };

  function getHeaderSectionId() {
    const headerEl = document.querySelector('[data-section-type="header"]');
    return headerEl?.dataset?.sectionId || 'header';
  }

  function getCartDrawerSectionId() {
    const drawer = document.querySelector(SELECTORS.drawer);
    return drawer?.dataset?.sectionId || null;
  }

  function getSectionRequestConfig() {
    const sections = [getHeaderSectionId()];
    const cartDrawerSectionId = getCartDrawerSectionId();

    if (cartDrawerSectionId) {
      sections.push(cartDrawerSectionId);
    }

    return {
      sections: sections.join(','),
      sections_url: `${window.location.pathname}${window.location.search}`
    };
  }

  class CartDrawerInstance {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.headerSectionId = getHeaderSectionId();
      this.isOpen = false;
      this.pendingRequest = null;
      this.debouncedChange = this.debounce(this.changeLine.bind(this), 300);

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleContainerClick = this.handleContainerClick.bind(this);

      this.bindEvents();
    }

    bindEvents() {
      if (this.container.dataset.cartDrawerBound === 'true') return;
      this.container.dataset.cartDrawerBound = 'true';

      this.container.addEventListener('click', this.handleContainerClick);
    }

    bindGlobalEvents() {
      if (document.documentElement.dataset.cartDrawerGlobalBound === 'true') return;
      document.documentElement.dataset.cartDrawerGlobalBound = 'true';

      document.addEventListener('click', (event) => {
        const instance = window.theme.CartDrawer?.getInstance();
        if (instance) instance.handleDocumentClick(event);
      });
      document.addEventListener('keydown', (event) => {
        const instance = window.theme.CartDrawer?.getInstance();
        if (instance) instance.handleKeydown(event);
      });
      document.addEventListener('cart:refresh', (event) => {
        const openAfter = Boolean(event.detail?.openAfter);
        window.theme.CartDrawer?.refresh({ openAfter });
      });
      document.addEventListener('cart:open', () => {
        window.theme.CartDrawer?.open();
      });
    }

    destroy() {
      this.container.removeEventListener('click', this.handleContainerClick);
      delete this.container.dataset.cartDrawerBound;
    }

    handleDocumentClick(event) {
      const openTrigger = event.target.closest(SELECTORS.openTrigger);
      if (openTrigger) {
        event.preventDefault();
        this.open();
        return;
      }

      if (!this.isOpen) return;

      if (event.target.closest(SELECTORS.closeTrigger)) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest(SELECTORS.overlay)) {
        event.preventDefault();
        this.close();
        return;
      }

      if (!event.target.closest(SELECTORS.panel) && !event.target.closest(SELECTORS.openTrigger)) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    handleContainerClick(event) {
      const qtyButton = event.target.closest(SELECTORS.qtyButton);
      if (qtyButton && !qtyButton.disabled) {
        event.preventDefault();
        const line = parseInt(qtyButton.dataset.line, 10);
        const quantity = parseInt(qtyButton.dataset.quantity, 10);
        if (!Number.isNaN(line) && !Number.isNaN(quantity)) {
          this.debouncedChange(line, quantity);
        }
        return;
      }

      const removeButton = event.target.closest(SELECTORS.removeButton);
      if (removeButton) {
        event.preventDefault();
        const line = parseInt(removeButton.dataset.line, 10);
        if (!Number.isNaN(line)) {
          this.changeLine(line, 0);
        }
      }
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.container.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-open');

      const closeButton = this.container.querySelector(SELECTORS.closeTrigger);
      if (closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.container.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-open');
    }

    getSectionsParam() {
      return getSectionRequestConfig().sections;
    }

    getSectionsUrl() {
      return getSectionRequestConfig().sections_url;
    }

    fetchSections() {
      const sections = this.getSectionsParam();
      const baseUrl = this.getSectionsUrl();
      const joiner = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${joiner}sections=${encodeURIComponent(sections)}`;
    }

    refresh(options = {}) {
      const { openAfter = false } = options;

      return fetch(this.fetchSections(), {
        method: 'GET',
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          this.renderSections(data);
          if (openAfter) this.open();
        })
        .catch((error) => {
          console.error(error);
        });
    }

    changeLine(line, quantity) {
      if (this.pendingRequest) {
        this.pendingRequest.abort();
      }

      this.container.classList.add('is-loading');
      this.pendingRequest = new AbortController();

      const payload = {
        line,
        quantity,
        sections: this.getSectionsParam(),
        sections_url: this.getSectionsUrl()
      };

      return fetch(`${window.theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload),
        signal: this.pendingRequest.signal
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) {
            const message = body.description || body.message || 'Unable to update cart';
            throw new Error(message);
          }

          if (body.sections) {
            this.renderSections(body.sections, body.item_count);
          } else {
            return this.refresh();
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          return this.refresh();
        })
        .finally(() => {
          this.container.classList.remove('is-loading');
          this.pendingRequest = null;
        });
    }

    getSectionHtml(sectionsData, sectionId) {
      if (!sectionsData || !sectionId) return null;

      if (sectionsData[sectionId]) {
        return sectionsData[sectionId];
      }

      const match = Object.keys(sectionsData).find((key) => key === sectionId || key.endsWith(`/${sectionId}`));
      return match ? sectionsData[match] : null;
    }

    renderSections(sectionsData, itemCount) {
      const drawerHtml = this.getSectionHtml(sectionsData, this.sectionId);

      if (drawerHtml) {
        const template = document.createElement('template');
        template.innerHTML = drawerHtml.trim();
        let newDrawer = template.content.querySelector(SELECTORS.drawer);

        if (!newDrawer) {
          const sectionRoot = template.content.querySelector('[data-section-type="cart-drawer"]');
          if (sectionRoot) {
            newDrawer = sectionRoot.matches(SELECTORS.drawer)
              ? sectionRoot
              : sectionRoot.querySelector(SELECTORS.drawer);
          }
        }

        if (newDrawer) {
          const wasOpen = this.isOpen;
          const scrollTop = this.container.querySelector(SELECTORS.body)?.scrollTop || 0;

          this.container.innerHTML = newDrawer.innerHTML;
          this.container.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');

          const body = this.container.querySelector(SELECTORS.body);
          if (body) body.scrollTop = scrollTop;

          if (wasOpen) {
            document.body.classList.add('cart-open');
          }
        }
      }

      const headerHtml = this.getSectionHtml(sectionsData, this.headerSectionId);
      if (headerHtml) {
        this.updateHeaderCart(headerHtml, itemCount);
      } else if (itemCount !== undefined) {
        this.updateHeaderCartCount(itemCount);
      }
    }

    updateHeaderCartCount(itemCount) {
      document.querySelectorAll(SELECTORS.headerCartCount).forEach((countEl) => {
        countEl.textContent = String(itemCount);
      });
    }

    updateHeaderCart(headerHtml, itemCount) {
      const template = document.createElement('template');
      template.innerHTML = headerHtml.trim();

      const headerCartSelectors = [
        '.page-header .header-cart',
        '.docked-navigation-container .header-cart',
        '.site-header__utils-account-cart .header-cart',
        '.site-header__actions .header-cart'
      ];

      headerCartSelectors.forEach((selector) => {
        const current = document.querySelector(selector);
        const updated = template.content.querySelector(selector);
        if (current && updated) {
          current.replaceWith(updated.cloneNode(true));
        }
      });

      if (itemCount !== undefined) {
        this.updateHeaderCartCount(itemCount);
        return;
      }

      const countFromHeader = template.content.querySelector(SELECTORS.headerCartCount);
      if (countFromHeader) {
        document.querySelectorAll(SELECTORS.headerCartCount).forEach((countEl) => {
          countEl.textContent = countFromHeader.textContent;
        });
      }
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  class CartDrawer {
    constructor() {
      this.instances = new WeakMap();
      this.init();
      document.addEventListener('shopify:section:load', (event) => {
        const drawer = event.target.querySelector(SELECTORS.drawer);
        if (drawer) this.register(drawer);
      });
      document.addEventListener('shopify:section:unload', (event) => {
        const drawer = event.target.querySelector(SELECTORS.drawer);
        if (drawer) this.unregister(drawer);
      });
    }

    init() {
      document.querySelectorAll(SELECTORS.drawer).forEach((drawer) => this.register(drawer));
    }

    register(container) {
      if (this.instances.has(container)) return;
      const instance = new CartDrawerInstance(container);
      instance.bindGlobalEvents();
      this.instances.set(container, instance);
    }

    unregister(container) {
      const instance = this.instances.get(container);
      if (instance) {
        instance.destroy();
        this.instances.delete(container);
      }
    }

    getInstance() {
      const drawer = document.querySelector(SELECTORS.drawer);
      return drawer ? this.instances.get(drawer) : null;
    }

    getSectionRequestConfig() {
      return getSectionRequestConfig();
    }

    open() {
      this.getInstance()?.open();
    }

    close() {
      this.getInstance()?.close();
    }

    refresh(options) {
      return this.getInstance()?.refresh(options);
    }

    renderSectionsFromAjax(sectionsData, itemCount) {
      this.getInstance()?.renderSections(sectionsData, itemCount);
    }
  }

  window.theme = window.theme || {};
  window.theme.CartDrawer = new CartDrawer();
})();
